import FollowButton from "@/components/FollowButton";
import ShareDropdown from "@/components/ShareDropdown";
import useAppStore from "@/context/store";
import { getSeoInlinks } from "@/services/qingzhou";
import { useListenScrollDirection } from "@/utils/hooks/useListenScrollDirection";
import imageUrl from "@/utils/imageUrl";
import { serverSideNotFound } from "@/utils/server/serverSide";
import useRequest from "@/utils/useReq";
import { useSetState } from "ahooks";
import { Button } from "antd";
import classNames from "classnames";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { Else, If, Then, When } from "react-if";
import HBEmpty from "../../components/HBEmpty";
import HBIcon from "../../components/HBIcon";
import { useLogin } from "../../components/LoginModal";
import PinList from "../../components/PinList";
import { followExplore, getExplorePins, unfollowExplore } from "../../services";
import styles from "./index.module.less";
export async function getServerSideProps({ query, req, res }) {
  const result = await getExplorePins({
    urlname: encodeURIComponent(query.urlname),
    limit: 20,
  });

  if (result.err) {
    return serverSideNotFound(res);
  }

  const inlinks = await getSeoInlinks({
    title: `${result?.name}专题_${result?.name}图片素材-花瓣网`,
    current_url: req.url,
  });

  return {
    props: {
      serverSideName: result?.name,
      serverSidePins: result?.pins,
      inlinks,
    },
  };
}

function ExploreEmpty() {
  return (
    <HBEmpty
      className={styles.empty}
      size="small"
      image="//cdn.dancf.com/fe-assets/img/6a5695b2efbda504b26a212d160553ec.svg"
      title="还没有采集哦~"
    />
  );
}

export default function Explore({ serverSideName, serverSidePins }) {
  const router = useRouter();
  const { urlname, backUrl } = router.query;
  const { login } = useLogin();
  const user = useAppStore((s) => s.user);
  const { headerHeight: height } = useAppStore(({ headerHeight }) => ({
    headerHeight,
  }));
  const headerRef = useRef(null);

  const [query, setQuery] = useSetState({
    max: 0,
    urlname: null,
  });

  const [name, setName] = useState(serverSideName ?? "");
  const [explore, setExplore] = useState(null);
  const [following, setFollowing] = useState(false);
  const [hasMore, setHasMore] = useState(serverSidePins?.length !== 0);
  const [dataSource, setDataSource] = useState(serverSidePins ?? []);
  const [showHeader, setShowHeader] = useState(false);
  const { runAsync: runFollow } = useRequest(followExplore, {
    manual: true,
  });
  const { runAsync: runUnfollow } = useRequest(unfollowExplore, {
    manual: true,
  });
  useRequest(
    async () => {
      const { max, urlname } = query;

      if (!urlname) {
        return;
      }

      const result = await getExplorePins({
        urlname,
        limit: 20,
        ...(max ? { max } : null),
      });

      if (result?.name && !name) {
        setName(result.name);
      }

      setExplore(result);

      if (typeof result?.following !== "undefined") {
        setFollowing(result.following);
      }

      const pins = result?.pins || [];

      if (pins.length <= 0) {
        setHasMore(false);
      }

      setDataSource(dataSource.concat(pins));
    },
    {
      refreshDeps: [query],
    }
  );

  const onLoadMore = () => {
    if (dataSource.length > 0) {
      const last = dataSource[dataSource.length - 1];
      setQuery({
        max: last.seq || last.pin_id,
      });
    }
  };

  const onToggleFollow = async () => {
    if (!user) {
      const result = await login({
        source_location: "explore关注",
      });
      if (!result) {
        return;
      }
    }

    if (following) {
      const result = await runUnfollow(urlname);
      if (!result.err) {
        setFollowing(false);
      }
    } else {
      const result = await runFollow(urlname);
      if (!result.err) {
        setFollowing(true);
      }
    }
  };

  useEffect(() => {
    if (urlname) {
      setFollowing(false);
      setName("");
      setDataSource([]);
      setQuery({
        urlname,
        max: 0,
      });
    }
  }, [urlname, setQuery]);

  // useEffect(() => {
  //   const onScroll = () => {
  //     const rect = headerRef.current?.getBoundingClientRect();
  //     setShowHeader(rect?.bottom - height <= 0);
  //   };

  //   window.addEventListener("scroll", onScroll);

  //   return () => {
  //     window.removeEventListener("scroll", onScroll);
  //   };
  // }, [height]);
  useListenScrollDirection(
    () => {
      const rect = headerRef.current?.getBoundingClientRect();
      setShowHeader(rect?.bottom - height <= 0);
    },
    () => {
      const rect = headerRef.current?.getBoundingClientRect();
      const showHeader =
        rect?.bottom - height <= 0 && rect?.bottom - height >= -120;
      setShowHeader(showHeader);
    }
  );

  return (
    <div
      className={styles.explore + " calculateWrapper"}
      data-content-source="信息流"
      data-topic-id={explore?.keyword_id}
      data-topic-title={name}
    >
      <Head>
        <title>
          {name ? `${name}专题_${name}图片素材-花瓣网` : "专题-花瓣网"}
        </title>
        {explore?.description ? (
          <meta name="description" content={explore.description} />
        ) : (
          <meta
            name="description"
            content={`花瓣网为您推荐${name}相关图片素材资源，其中涵盖众多设计师收藏采集的${name}相关图片素材资源。花瓣网，设计师寻找灵感的天堂！`}
          />
        )}
        {explore?.related_words && explore?.related_words.length > 0 ? (
          <meta
            name="keywords"
            content={explore?.related_words?.join() + "," + name}
          />
        ) : name ? (
          <meta
            name="keywords"
            content={`${name},${name}图片,${name}素材,${name}资源`}
          />
        ) : (
          false
        )}
      </Head>
      <div
        className={classNames([
          styles.sticky,
          showHeader ? styles.show : null,
          "modalOpenWidth",
        ])}
        style={{
          top: `${height}px`,
        }}
      >
        <div
          className={classNames(styles.wrapper, "calculateWrapper")}
          // style={{
          //   width: `${width}px`,
          // }}
        >
          <div className={styles.back}>
            <When condition={backUrl}>
              <Button
                className={styles.button}
                type="link"
                onClick={() => router.push(backUrl)}
                icon={
                  <HBIcon className={styles.icon} type="ic_arrowThinLeft" />
                }
              />
            </When>
          </div>
          <div className={styles.title}>{name || ""}</div>
          <div className={styles.operation}>
            <ShareDropdown
              placement="bottomCenter"
              pics={
                dataSource?.length > 0 ? imageUrl(dataSource[0].file) : null
              }
              title={`这个来自「花瓣网」的专题不错哦，有很多${name}相关的设计灵感，来看看：`}
            >
              <Button
                className={styles.share}
                type="text"
                title="分享"
                shape="circle"
              >
                <HBIcon type="hb_share_new" />
              </Button>
            </ShareDropdown>
            <FollowButton
              className={styles.button}
              followed={following}
              onClick={onToggleFollow}
              data-gd-click="button_click"
              data-button-name={following ? "取消关注" : "关注"}
              data-concern-object-type="explore"
              data-concern-object-name={name}
            />
          </div>
        </div>
      </div>
      <div className={styles.header} ref={headerRef}>
        <div className={styles.wrapper}>
          <div className={styles.back}>
            <When condition={backUrl}>
              <Button
                className={styles.button}
                type="link"
                onClick={() => router.push(backUrl)}
                icon={
                  <HBIcon className={styles.icon} type="ic_arrowThinLeft" />
                }
              />
            </When>
          </div>
          <h1 className={styles.title}>{name || ""}</h1>
          <div className={styles.operation}>
            <ShareDropdown
              placement="bottomCenter"
              pics={
                dataSource?.length > 0 ? imageUrl(dataSource[0].file) : null
              }
              title={`这个来自「花瓣网」的专题不错哦，有很多${name}相关的设计灵感，来看看：`}
            >
              <Button
                className={styles.share}
                type="text"
                title="分享"
                shape="circle"
              >
                <HBIcon type="hb_share_new" />
              </Button>
            </ShareDropdown>

            <FollowButton
              followed={following}
              onClick={onToggleFollow}
              data-gd-click="button_click"
              data-button-name={following ? "取消关注" : "关注"}
              data-concern-object-type="explore"
              data-concern-object-name={name}
            />
          </div>
        </div>
      </div>
      <If condition={dataSource.length > 0 || hasMore}>
        <Then>
          <PinList
            dataSource={dataSource}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
          />
        </Then>
        <Else>
          <ExploreEmpty />
        </Else>
      </If>
    </div>
  );
}
